import React from 'react'
import {
  Button,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { AddIcon } from '@chakra-ui/icons'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import xhr from 'xhr'
import getCountries from 'api/index/getCountries'
import { BRAND, EmptyState } from '../../../components/ui'
import RegisterStudentModal from './RegisterStudentModal'
import LinkStudentModal from './LinkStudentModal'
import CredentialsModal from './CredentialsModal'

const fullName = (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim()

// Estudiantes vinculados a un prospecto. Un mismo contacto (p. ej. un
// acudiente) puede inscribir a varios estudiantes.
export default function StudentsProspect({ prospect, onChange }) {
  const [students, setStudents] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [countries, setCountries] = React.useState([])

  const [registerOpen, setRegisterOpen] = React.useState(false)
  const [linkOpen, setLinkOpen] = React.useState(false)
  const [linkStudentId, setLinkStudentId] = React.useState(null)
  const [credentials, setCredentials] = React.useState(null)

  const loadStudents = React.useCallback(async () => {
    try {
      setLoading(true)
      const res = await xhr(
        `/students?filters[origin_prospect][id][$eq]=${prospect.id}&populate[user]=true&sort[0]=createdAt:desc&pagination[limit]=50`
      )
      setStudents(res.data.data)
      onChange?.({ count: res.data.data.length })
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [prospect.id])

  React.useEffect(() => {
    loadStudents()
    getCountries()
      .then(setCountries)
      .catch((error) => console.error(error))
  }, [loadStudents])

  const handleCreated = (result) => {
    setRegisterOpen(false)
    setCredentials(result)
    loadStudents()
    onChange?.({ statusChanged: true })
  }

  const handleLinkExisting = (studentId) => {
    setRegisterOpen(false)
    setLinkStudentId(studentId)
    setLinkOpen(true)
  }

  const handleLinked = () => {
    setLinkOpen(false)
    setLinkStudentId(null)
    loadStudents()
    onChange?.({ statusChanged: true })
  }

  const actions = (
    <Flex gap={3} wrap="wrap" justify="center">
      <Button variant="outline" colorScheme="purple" size="sm" onClick={() => setLinkOpen(true)}>
        Vincular existente
      </Button>
      <Button
        size="sm"
        leftIcon={<AddIcon boxSize={2.5} />}
        bg={BRAND}
        color="white"
        _hover={{ bg: '#463fd1' }}
        onClick={() => setRegisterOpen(true)}
      >
        Registrar estudiante
      </Button>
    </Flex>
  )

  return (
    <>
      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner color={BRAND} />
        </Flex>
      ) : students.length === 0 ? (
        <EmptyState
          title="Aún no hay estudiantes vinculados"
          description="Un contacto puede consultar por uno o varios estudiantes. Registra o vincula cada cuenta por separado."
          action={actions}
        />
      ) : (
        <>
          <Flex justify="flex-end" mb={4}>
            {actions}
          </Flex>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Estudiante</Th>
                  <Th>Contacto</Th>
                  <Th>Cuenta creada</Th>
                  <Th textAlign="right">Detalle</Th>
                </Tr>
              </Thead>
              <Tbody>
                {students.map((student) => (
                  <Tr key={student.id}>
                    <Td py={3}>
                      <Text fontWeight="600">{fullName(student.user) || 'Sin nombre'}</Text>
                      <Text fontSize="xs" color="gray.500">
                        Código {student.id}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{student.user?.email}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {student.user?.cellphone}
                      </Text>
                    </Td>
                    <Td fontSize="sm">{dayjs(student.user?.createdAt || student.createdAt).format('DD/MM/YYYY')}</Td>
                    <Td textAlign="right">
                      <Button
                        as={Link}
                        to={`/plataforma/asesoras/ver-estudiante/${student.id}`}
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                      >
                        Ver estudiante
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      )}

      <RegisterStudentModal
        isOpen={registerOpen}
        onClose={() => setRegisterOpen(false)}
        prospect={prospect}
        countries={countries}
        onCreated={handleCreated}
        onLinkExisting={handleLinkExisting}
      />
      <LinkStudentModal
        isOpen={linkOpen}
        onClose={() => {
          setLinkOpen(false)
          setLinkStudentId(null)
        }}
        prospect={prospect}
        initialStudentId={linkStudentId}
        onLinked={handleLinked}
      />
      <CredentialsModal result={credentials} onClose={() => setCredentials(null)} />
    </>
  )
}
